import * as React from "react";
import { useSelector } from "react-redux";
import { leaderboardLoading } from "../redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy, faUsers } from "@fortawesome/free-solid-svg-icons";
import Avatar from "./Avatar";

interface HealthLeaderboardCardProps {
  name: string;
  steps: string;
  image: string;
  place: number;
  backgroundColor: string;
  isTopTeamView: boolean;
  type: string;
}

const Badge = ({
  iconBackgroundColor,
  iconColor,
  isTrophy,
}: {
  iconBackgroundColor: string;
  iconColor: string;
  isTrophy: boolean;
}) => {
  const icon = isTrophy ? faTrophy : faUsers;
  return (
    <div
      className="w-12 h-12 rounded-full border-4 flex felx-col justify-center items-center"
      style={{ background: iconBackgroundColor }}
    >
      <FontAwesomeIcon size={"1x"} color={iconColor} icon={icon} />
    </div>
  );
};

const HealthLeaderboardCard = ({
  name,
  image,
  place,
  steps,
  isTopTeamView,
  type,
  backgroundColor,
}: HealthLeaderboardCardProps) => {
  const loading = useSelector(leaderboardLoading);

  const iconColor =
    place === 1
      ? "#BC9E28"
      : place === 2
      ? "#9C9C9C"
      : place === 3
      ? "#AE6820"
      : "#BDBDBD";
  const iconBackgroundColor =
    place === 1
      ? "#FFE46B"
      : place === 2
      ? "#DFDFDF"
      : place === 3
      ? "#EBA658"
      : "#E0E0E0";

  if (loading)
    return (
      <div
        className="mt-5 p-3 border-4 border-gray-50 rounded-md h-14"
        style={{ background: "#F6F7F8", width: "100%" }}
      />
    );

  return (
    <div
      className="flex flex-row justify-between items-center mt-5 p-3 border-4 border-gray-50 rounded-md"
      style={{ background: backgroundColor }}
    >
      <div className="flex flex-row items-center">
        <div className="font-semibold text-lg mr-5">
          {getPaddedNumber(place)}
        </div>

        <div className="flex flex-row">
          {isTopTeamView ? (
            <Badge
              iconBackgroundColor={iconBackgroundColor}
              iconColor={iconColor}
              isTrophy={place < 4}
            />
          ) : (
            <Avatar
              fullName={name}
              url={image}
              className="rounded-full w-12 border-4 border-gray-300"
            />
          )}

          <div className="flex flex-col ml-3">
            <div className="font-semibold text-lg">
              {isTopTeamView ? name : getDisplayName(name)}
            </div>
            <div className="text-gray-400 text-sm font-semibold">{`${steps} ${
              type === "top-team" ? "avg steps" : "steps"
            }`}</div>
          </div>
        </div>
      </div>

      {place < 4 && !isTopTeamView && (
        <Badge
          iconBackgroundColor={iconBackgroundColor}
          iconColor={iconColor}
          isTrophy
        />
      )}
    </div>
  );
};

/**
 * Returns a string padded with 0 if the number only
 * has 1 digit, else return the number.
 *
 * @param {number} number
 */
function getPaddedNumber(number: number) {
  let num = number + "";
  if (num.length === 1) {
    num = "0" + num;
  }
  return num;
}

export function getDisplayName(name: string) {
  const names = name.split(" ");
  let displayName = names[0];

  if (names.length > 1) {
    for (let j = 1; j < names.length; j++) {
      displayName += " " + names[j].substring(0, 1).toUpperCase();
    }
  }
  return displayName;
}

export default HealthLeaderboardCard;
